/**
 * Centralizes dynamic route resolution, lazy component loading with chunk-error
 * recovery, and route prefetching. Uses sessionStorage to guard against reload loops
 * when the app is updated while a tab is open.
 */
import React from "react";

const CHUNK_RELOAD_KEY = "pms_chunk_reload_once";
const CHUNK_ERROR_PATTERNS = [
  "ChunkLoadError",
  "Loading chunk",
  "Failed to fetch dynamically imported module",
  "dynamically imported module",
];

export interface NormalizedAppRoute {
  readonly key: string;
  readonly path: string;
  readonly component: string;
  readonly loadedProps: unknown;
  readonly application: string;
}

type AccessibleApp = {
  ApplicationID?: number;
  Application?: string;
  Path?: string;
  Component?: string;
  AppProps?: string | null;
  params?: string;
  childApps?: AccessibleApp[];
};

type LazyModule = { default: React.ComponentType<any> };
type ModuleLoader = () => Promise<LazyModule>;

const componentsMap = new Map<string, React.LazyExoticComponent<React.ComponentType<any>>>();

const modules = import.meta.glob([
  "../pdi/**/*.tsx",
  "../pdi/**/*.jsx",
  "../pdi/**/*.js",
  "../pms/**/*.tsx",
  "../pms/**/*.jsx",
  "../pms/**/*.js",
  "../framework/**/*.tsx",
  "../framework/**/*.jsx",
  "../framework/**/*.js",
  "../other/**/*.tsx",
  "../other/**/*.jsx",
  "../other/**/*.js",
  "!../**/*.test.ts",
  "!../**/*.test.tsx",
  "!../**/*.test.js",
  "!../**/*.test.jsx",
]) as Record<string, ModuleLoader>;

const parseAppProps = (appProps: string | null | undefined, componentPath: string): unknown => {
  if (!appProps) return null;
  try {
    return JSON.parse(appProps);
  } catch (error) {
    console.error(`Failed to parse AppProps for ${componentPath}:`, error, "Raw value:", appProps);
    return null;
  }
};

const addRouteIfValid = (routes: NormalizedAppRoute[], app: AccessibleApp, fallbackKey: string) => {
  const path = app.Path;
  const component = app.Component;

  if (!path || !component) {
    return;
  }

  const normalizedParam = app.params?.trim();
  const fullPath = path + (normalizedParam ? `/:${normalizedParam}` : "");
  routes.push({
    key: String(app.ApplicationID ?? `${fullPath}:${component}:${fallbackKey}`),
    path: fullPath,
    component,
    loadedProps: parseAppProps(app.AppProps, component),
    application: String(app.Application ?? ""),
  });
};

/** Flattens the nested AccessibleApps tree from the auth context into a flat list of validated routes with pre-parsed props. */
export const normalizeAccessibleRoutes = (accessibleApps: unknown[]): readonly NormalizedAppRoute[] => {
  const routes: NormalizedAppRoute[] = [];

  (accessibleApps as AccessibleApp[]).forEach((app, index) => {
    addRouteIfValid(routes, app, `parent-${index}`);
    app.childApps?.forEach((childApp, childIndex) => {
      addRouteIfValid(routes, childApp, `child-${index}-${childIndex}`);
    });
  });

  return routes;
};

/** Resolves a component path (e.g. `./pms/Foo`) to the glob key used by import.meta.glob. Handles `./` to `../` rewriting (since this file lives in src/routing/) and tries .tsx/.jsx/.js/index.* extensions. */
const resolveModulePath = (component: string): string | null => {
  const baseComponentPath = component.startsWith("./")
    ? `../${component.slice(2)}`
    : component;

  const normalizedComponent = baseComponentPath.endsWith(".tsx") ||
    baseComponentPath.endsWith(".jsx") ||
    baseComponentPath.endsWith(".js")
    ? baseComponentPath
    : null;

  if (normalizedComponent) {
    return modules[normalizedComponent] ? normalizedComponent : null;
  }

  const attempts = [
    `${baseComponentPath}.tsx`,
    `${baseComponentPath}.jsx`,
    `${baseComponentPath}.js`,
    `${baseComponentPath}/index.tsx`,
    `${baseComponentPath}/index.jsx`,
    `${baseComponentPath}/index.js`,
  ];

  for (const attempt of attempts) {
    if (modules[attempt]) {
      return attempt;
    }
  }

  return null;
};

/** Returns true if the error message matches known chunk/dynamic-import failure patterns. */
export const isLikelyChunkLoadError = (error: unknown): boolean => {
  const message = String(
    (error as { message?: string })?.message ??
    (error as { toString?: () => string })?.toString?.() ??
    ""
  );
  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

export const shouldTryAutoReload = () => {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_KEY) !== "1";
  } catch (error) {
    console.warn("[chunk-recovery] Failed to read reload guard from sessionStorage.", error);
    return false;
  }
};

export const markAutoReloadAttempt = (): boolean => {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    return true;
  } catch (error) {
    console.warn("[chunk-recovery] Failed to persist reload guard to sessionStorage.", error);
    return false;
  }
};

export const clearAutoReloadAttempt = () => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch (error) {
    console.warn("[chunk-recovery] Failed to clear reload guard from sessionStorage.", error);
  }
};

const toChunkRecoveryError = (error: unknown) => {
  const wrapped = new Error(
    "The application was updated and this page is using stale assets. Please refresh."
  ) as Error & { isChunkLoadError?: boolean; cause?: unknown };
  wrapped.isChunkLoadError = true;
  wrapped.cause = error;
  return wrapped;
};

/** Attempts a one-shot page reload to recover from stale chunk errors. Returns true if a reload was triggered. Uses sessionStorage to prevent infinite reload loops. */
export const tryAutoRecoverChunkFailure = (error: unknown): boolean => {
  if (!isLikelyChunkLoadError(error) || !shouldTryAutoReload()) {
    return false;
  }

  const flagWritten = markAutoReloadAttempt();
  if (!flagWritten) {
    return false;
  }

  console.warn("[chunk-recovery] Reloading once to recover from stale assets.");
  window.location.reload();
  return true;
};

/** Wraps a module loader with chunk-error recovery. On success, clears the reload guard. On chunk failure: triggers a page reload (first attempt) or throws a wrapped error (subsequent attempts). Non-chunk errors are re-thrown as-is. */
const importWithRetry = async (loader: ModuleLoader): Promise<LazyModule> => {
  try {
    const mod = await loader();
    clearAutoReloadAttempt();
    return mod;
  } catch (error) {
    if (!isLikelyChunkLoadError(error)) {
      throw error;
    }

    if (typeof window !== "undefined" && shouldTryAutoReload()) {
      const flagWritten = markAutoReloadAttempt();
      if (flagWritten) {
        window.location.reload();
        // Keep Suspense pending while the browser reloads. If reload fails
        // (sandboxed iframe, extension), fall back to the ErrorBoundary after a timeout.
        return new Promise<LazyModule>((_, reject) => {
          setTimeout(() => reject(toChunkRecoveryError(error)), 8_000);
        });
      }
    }

    throw toChunkRecoveryError(error);
  }
};

/** Eagerly loads a component module so it is cached for instant navigation. Uses a plain import (no retry/reload) to avoid unexpected page reloads on hover. */
export const preloadComponent = async (component: string): Promise<void> => {
  const modulePath = resolveModulePath(component);
  if (!modulePath) {
    return;
  }

  try {
    await modules[modulePath]();
  } catch {
    // Silently fail -- actual navigation will handle recovery via importWithRetry.
  }
};

/** Returns a React.lazy component for the given module path, with caching for resolved modules. Missing modules return an uncached fallback so subsequent attempts can re-evaluate. */
export const getLazyComponent = (component: string) => {
  if (componentsMap.has(component)) {
    return componentsMap.get(component)!;
  }

  const modulePath = resolveModulePath(component);
  if (!modulePath) {
    console.error(`Module not found: ${component}`);
    // Do NOT cache the fallback -- subsequent attempts should re-evaluate in case the failure was transient.
    const MissingComponent = React.lazy(() =>
      Promise.resolve({
        default: () => React.createElement("div", null, `Component not found: ${component}`),
      })
    );
    return MissingComponent;
  }

  const Component = React.lazy(() => importWithRetry(modules[modulePath]));
  componentsMap.set(component, Component);
  return Component;
};
